import { fg } from '@atlaskit/platform-feature-flags';
import { SORT_FIELD_KEY } from '@atlassian/search-client';
import { type BackendExperiment, getBackendExperiment } from '@atlassian/search-experiment';

import {
	getDefaultEntityAti,
	RESULT_LIMIT_FOR_MULTIPLE_PRODUCTS,
	RESULT_LIMIT_FOR_SINGLE_PRODUCT,
} from '../../../common/constants/entities';
import { SEARCH_PAGE } from '../../../common/constants/experience';
import {
	PrimaryProductKeys,
	productsSupportingEmptyQuery,
} from '../../../common/constants/products';
import type { BootstrapVariables, SearchVariables } from '../../../common/constants/relay/types';
import { getSessionIdFromLocalStorage } from '../../../common/utils/events/utils';
import { serve3PClickIngestionExperiment } from '../../../common/utils/experiments';
import { serveAutoAppliedFiltersExperiment } from '../../../common/utils/experiments/experiment';
import { serveFederatedSlackExperiment } from '../../../common/utils/experiments/experiment-utils';
import { isHello } from '../../../common/utils/is-hello';
import { getSelectedFilters } from '../../../controllers/filters/selected';
import { getJiraFiltersForResultsCountQuery } from '../../../controllers/results/utils';
import { getFanoutExperimentCohort } from '../../../services/search-query-generator/experiments';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { rightPanelQuery$variables } from '../right-panel/__generated__/rightPanelQuery.graphql';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { searchResultsQuery$variables } from '../search-results/__generated__/searchResultsQuery.graphql';

type GetVariablesArgs = {
	bootstrap: BootstrapVariables;
	searchQuery: SearchVariables['searchQuery'];
	originalQuery: SearchVariables['originalQuery'];
	resultsSort: SearchVariables['resultsSort'];
	filters: SearchVariables['filters'];
	selectedProducts: SearchVariables['selectedProducts'];
	selectedDatasource: SearchVariables['selectedDatasource'];
	jsmIncidentFilterIds: SearchVariables['jsmIncidentFilterIds'];
	paginationTokens: SearchVariables['paginationTokens'];
	overrideSearchSessionId?: string | null;
};

// eslint-disable-next-line @repo/internal/deprecations/deprecation-ticket-required
/**
 * Gets the variables for both search results and right panel queries.
 *
 * @deprecated Use {@link packages/search/search-page/src/services/search-query-generator/index.ts} to make changes instead.
 */
export const getVariables = ({
	bootstrap,
	searchQuery,
	originalQuery,
	resultsSort: sort,
	filters,
	selectedProducts,
	selectedDatasource,
	jsmIncidentFilterIds,
	paginationTokens,
	overrideSearchSessionId,
}: GetVariablesArgs): [
	searchResultsQuery$variables,
	rightPanelQuery$variables,
	BackendExperiment,
] => {
	const {
		cloudId = '',
		primaryProduct,
		thirdPartyConfigs,
		experimentConfig,
		products,
		allFilterExcludedProducts,
		productEdition,
		productDataRegions,
		isJiraIssueIndexed,
		user,
		overrides,
	} = bootstrap;

	const searchSessionId = overrideSearchSessionId || bootstrap.searchSessionId;
	const fetchFromCursor = paginationTokens[0]?.cursor;

	const isEmptyQuery = !searchQuery || searchQuery.trim() === '';
	const availableProducts = products.filter(
		// If the query is empty, only some products are valid
		(product) => !isEmptyQuery || productsSupportingEmptyQuery.includes(product),
	);

	const {
		selectedFilters,
		entities,
		unselectedProductsThirdPartyFilters,
		entitiesForResultsCountQuery,
	} = getSelectedFilters({
		filters,
		selectedProducts,
		selectedDatasource,
		availableProducts: products,
		allFilterExcludedProducts,
		thirdPartyConfigs,
		jsmIncidentFilterIds,
		currentUserId: user?.id,
	});

	const backendExperiment = getBackendExperiment({
		experimentConfig,
		entities,
		productEdition,
		isHello: isHello(cloudId),
		productDataRegions,
		experience: SEARCH_PAGE,
		tenantId: cloudId,
		isJiraIssueIndexed,
		thirdPartyFilters: selectedFilters.thirdPartyFilters,
		backendExperimentOverrides: overrides?.backendExperiments,
	});

	const { experimentId, shadowExperimentId, experimentLayers } = backendExperiment || {};

	const limit =
		entities.length === getDefaultEntityAti().length
			? RESULT_LIMIT_FOR_MULTIPLE_PRODUCTS
			: RESULT_LIMIT_FOR_SINGLE_PRODUCT;

	const {
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		assetsFilters,
		talentFilters,
		sortFilters,
	} = selectedFilters;

	const sortVariables = sort.field === SORT_FIELD_KEY.RELEVANCE ? [] : [sort];

	// If the original query param is present, we want to disable query replacement
	const disableQueryReplacement = !!originalQuery;

	const queryVersion = 1;
	const cloudIdARI =
		primaryProduct === PrimaryProductKeys.Jira && fg('jira_site_ari_query_enabled')
			? `ari:cloud:jira::site/${cloudId}`
			: `ari:cloud:confluence::site/${cloudId}`;
	const experience = SEARCH_PAGE;
	const tenantId = cloudId;

	const sessionId = getSessionIdFromLocalStorage();

	serveFederatedSlackExperiment(
		undefined,
		selectedProducts,
		{ fireExperimentExposure: true },
		thirdPartyConfigs,
	);

	const fanoutExperimentCohort = getFanoutExperimentCohort(selectedProducts);

	// To ensure rightPanel query matches as much as possible, we inherit+override both from shared variables
	const sharedQueryVariables = {
		cloudIdARI,
		commonFilters,
		confluenceFilters,
		entities,
		experience,
		jiraFilters,
		limit,
		mercuryFilters,
		compassFilters,
		query: searchQuery,
		queryVersion,
		searchSessionId,
		sort: fg('rovo_search_page_sort_filter_in_store') ? sortFilters : sortVariables,
		sourceProduct: primaryProduct,
		thirdPartyFilters,
		sessionId,
		tenantId,
		disableQueryReplacement,
		...(fg('assets_rovo_search_all') ? { assetsFilters } : {}),
		...(fg('talent_global_search') ? { talentFilters } : {}),
	};

	const searchResultsQueryVariables: searchResultsQuery$variables = {
		...sharedQueryVariables,
		// pagination is only valid for results
		after: fetchFromCursor,
		// highlighting is only useful for results
		enableHighlighting: true,
		// Experimentation is only valid in results section. Note that experiments can differ based on selected entites/products, so if we include experiments in counts, the counts will change as filters change
		experimentId,
		experimentLayers: experimentLayers?.map(({ name, layerId, shadowId, definitions }) => ({
			name,
			layerId,
			shadowId,
			definitions,
		})),
		shadowExperimentId,
		fanoutExperimentCohort,
		includeAssigneeField: fg('rovo_search_assignee_field_fps'),
		includeAutoAppliedFilters: serveAutoAppliedFiltersExperiment(),
		includeIconUrl: fg('rovo_search_confluence_icons_fps'),
		includePositions: fg('talent_global_search'),
		includeThirdPartyId: serve3PClickIngestionExperiment(thirdPartyConfigs, {
			fireExperimentExposure: false,
		}),
		includePassionfruitAsk: fg('platform_passionfruit_search_onboarding_enabled'),
		includeNavBoostScore: fg('kd_enable_nav_boost_score'),
		// Only fetch totalCounts when Gmail is selected (needed for federated Gmail count)
		includeTotalCounts:
			selectedProducts.length === 1 &&
			selectedProducts[0] === 'gmail' &&
			fg('formula_3p_gmail_result_count'),

		// It's recommended to use a Variable Provider to conditionally fetch data.
		// See this runbook for details: https://hello.atlassian.net/wiki/x/yx_OWgE
	};

	const rightPanelQueryVariables: rightPanelQuery$variables = {
		...sharedQueryVariables,
		// Limit and sort are irrelevant for counts
		limit: 0,
		sort: [],
		// Counts searches for approx all entities (see getSelectedFilters for edge cases)
		entities: entitiesForResultsCountQuery,
		thirdPartyFilters: unselectedProductsThirdPartyFilters,
		// We need to search for all jira product types to support counts in Jira/JSM/JPD correctly
		jiraFilters: getJiraFiltersForResultsCountQuery(jiraFilters, availableProducts),
	};

	return [searchResultsQueryVariables, rightPanelQueryVariables, backendExperiment];
};
